import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import {
  SubItemActiveModal,
  SubItemAttributeUpdate,
  SubItemModalType,
  SubItemPermissions,
  SubItemRootAsset,
  SubItemsContextValue,
} from '@atom/components/common/subItemTree/SubItemsContext';
import {
  collectSubtreeIds,
  detailChildIds,
  parentOf,
  SubItemTreeActionType,
} from '@atom/components/common/subItemTree/subItemTreeReducer';
import textConstants from '@atom/constants/textConstants';
import {
  APPROVE_ASSET_ATTRIBUTE_CHANGE,
  APPROVE_ASSET_CHANGES,
  CREATE_ASSET_ELEMENTS,
  DELETE_ASSET,
  REJECT_ASSET_ATTRIBUTE_CHANGE,
  REJECT_ASSET_CHANGES,
  UPDATE_ASSET,
} from '@atom/graph/asset';
import { useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { useStableCallback } from '@atom/hooks/useStableCallback';
import { Snackbar } from '@atom/mui';
import {
  AssetAttributeChangeInput,
  AssetElement,
  AssetElementsCreateInput,
  ChangeType,
} from '@atom/types/asset';
import { PolicyAction } from '@atom/types/policy';
import { hasAccess } from '@atom/utilities/accessUtilities';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { isOrgAdminRequiredForAssetAddition } from '@atom/utilities/tenantFeatureUtilities';
import { getErrorCode } from '@atom/utilities/validationUtilities';

import InventoryDetailContext from '../InventoryDetailContext';

type InventorySubItemTreeValue = {
  value: SubItemsContextValue;
  onMoveOrReplaceClose: () => Promise<void>;
};

const useInventorySubItemTree = (): InventorySubItemTreeValue => {
  const preferences = usePreferences();

  const {
    asset: rootAsset,
    subItemTree: tree,
    subItemTreeDispatch: dispatch,
    fetchAssetDetail,
    loadNode,
    refetchNodes,
    refreshChangeTypes,
    refreshSubItemTree,
  } = useContext(InventoryDetailContext);
  const [searchParamsObject, { updateSearchParams }] = useNavigation();

  const [activeModal, setActiveModal] = useState<SubItemActiveModal | null>(
    null,
  );
  const [approvalErrorOpen, setApprovalErrorOpen] = useState<boolean>(false);
  const [scrollToId, setScrollToId] = useState<string | null>(null);

  // Row handlers are props of the memoized SubItemRow, so keep their identity
  // fixed.
  const openModal = useCallback(
    (type: SubItemModalType, node: AssetElement) =>
      setActiveModal({ type, node }),
    [],
  );
  const onScrolled = useCallback(() => setScrollToId(null), []);
  const onToggleGroup = useCallback(
    (key: string) =>
      dispatch({ type: SubItemTreeActionType.TOGGLE_EXPANDED, data: { key } }),
    [dispatch],
  );

  const [assetUpdate] = useMutation(UPDATE_ASSET);
  const [assetDelete] = useMutation(DELETE_ASSET);
  const [assetChangesApprove] = useMutation(APPROVE_ASSET_CHANGES);
  const [assetChangesReject] = useMutation(REJECT_ASSET_CHANGES);
  const [assetElementsCreate] = useMutation(CREATE_ASSET_ELEMENTS);
  const [assetAttributeChangeApprove] = useMutation(
    APPROVE_ASSET_ATTRIBUTE_CHANGE,
  );
  const [assetAttributeChangeReject] = useMutation(
    REJECT_ASSET_ATTRIBUTE_CHANGE,
  );

  const rootId = rootAsset.id;
  const nodeIds = useMemo(() => Object.keys(tree.nodes), [tree.nodes]);
  const selectedId = (searchParamsObject.elementId as string) || null;

  const setElementId = (id: string | null) =>
    updateSearchParams({ elementId: id ?? undefined });

  // Row handlers are props of the memoized SubItemRow
  const onRowClick = useStableCallback((node: AssetElement) => {
    setElementId(node.id);

    if (node.hasChildren) {
      dispatch({
        type: SubItemTreeActionType.TOGGLE_EXPANDED,
        data: { key: node.id },
      });
    }

    const status = tree.status[node.id];

    if (status !== 'loaded' && status !== 'loading') {
      loadNode(node.id);
    }
  });

  // Fetch the named element, then expand the tree down to it.
  const expandToElement = async (elementId: string) => {
    const detail = await fetchAssetDetail(elementId);
    const ancestorIds = (detail.ancestors ?? []).map(ancestor => ancestor.id);

    // The link can name an element that lives under a different root asset
    // (e.g. a stale URL after a move). Stay on this asset's Sub Items tab and
    // drop the selection.
    if (!R.isEmpty(ancestorIds) && R.head(ancestorIds) !== rootId) {
      dispatch({
        type: SubItemTreeActionType.REMOVE_NODE,
        data: { id: elementId },
      });

      setElementId(null);
      return;
    }

    dispatch({
      type: SubItemTreeActionType.NODE_LOADED,
      data: { id: elementId, detail },
    });

    // Fetch every unloaded ancestor below the root in parallel.
    const { status } = tree;
    const intermediateIds = R.tail(ancestorIds).filter(
      id =>
        id !== elementId && status[id] !== 'loaded' && status[id] !== 'loading',
    );

    await refetchNodes(intermediateIds);

    dispatch({
      type: SubItemTreeActionType.EXPAND_PATH,
      data: { ids: R.uniq([...ancestorIds, elementId]) },
    });

    setScrollToId(elementId);
  };

  // A deep link and a row click are the same code path: the URL names a node
  // that isn't loaded, so resolve and expand to it.
  const resolveSelection = async (elementId: string) => {
    dispatch({
      type: SubItemTreeActionType.NODE_LOADING,
      data: { id: elementId },
    });

    try {
      await expandToElement(elementId);
    } catch (error) {
      dispatch({
        type: SubItemTreeActionType.NODE_ERROR,
        data: { id: elementId },
      });

      Snackbar.error({
        message:
          getErrorCode(error) === 404
            ? 'This sub item no longer exists.'
            : textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });

      setElementId(null);
    }
  };

  useEffect(() => {
    if (!rootId || !selectedId || selectedId === rootId) {
      return;
    }

    const { details, status } = tree;
    if (details[selectedId] || status[selectedId] === 'loading') {
      return;
    }

    resolveSelection(selectedId);
  }, [selectedId, rootId]);

  const renameElement = async (id: string, name: string) => {
    Snackbar.info({
      message: textConstants.GENERIC_APPLICATION_UPDATING_TEXT,
    });

    try {
      const { data } = await assetUpdate({
        variables: { input: { id, name } },
      });

      const updated = data.assetUpdate;
      dispatch({
        type: SubItemTreeActionType.NODE_LOADED,
        data: { id, detail: updated },
      });

      await refreshChangeTypes(nodeIds);
      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATED_TEXT,
        autoHideDuration: 3000,
      });
    } catch (error) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const deleteElement = async (node: AssetElement) => {
    Snackbar.info({
      message: textConstants.GENERIC_APPLICATION_DELETING_TEXT,
    });

    const subtreeIds = collectSubtreeIds(tree, node.id);
    try {
      await assetDelete({ variables: { input: { id: node.id } } });

      const parent = await loadNode(parentOf(tree, node.id) ?? rootId);

      if (!parent) {
        Snackbar.error({
          message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
        });
        return;
      }

      const childIds = detailChildIds(parent);
      const removedIds = childIds.includes(node.id) ? [] : subtreeIds;

      if (childIds.includes(node.id)) {
        await refetchNodes(
          subtreeIds.filter(id => tree.status[id] === 'loaded'),
        );
      }

      if (selectedId && removedIds.includes(selectedId)) {
        setElementId(null);
      }

      const remainingNodeIds = R.without(removedIds, nodeIds);
      await refreshChangeTypes(R.union(remainingNodeIds, childIds));

      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATED_TEXT,
        autoHideDuration: 3000,
      });
    } catch (error) {
      Snackbar.error({
        message:
          getErrorCode(error) === 422
            ? 'Work is currently associated with this inventory asset. Deletion is not allowed.'
            : textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const applyChanges = async (
    action: 'approve' | 'reject',
    node: AssetElement,
  ) => {
    Snackbar.info({
      message: textConstants.GENERIC_APPLICATION_UPDATING_TEXT,
    });

    try {
      const mutation =
        action === 'approve' ? assetChangesApprove : assetChangesReject;

      await mutation({
        variables: {
          input: {
            id: node.id,
            rootAssetId: rootId,
            includeChangesBefore: node.includeChangesBefore,
          },
        },
      });

      const nodeRemoved =
        (action === 'reject' && node.changeType === ChangeType.ADDED) ||
        (action === 'approve' && node.changeType === ChangeType.DELETED);

      if (nodeRemoved) {
        const removedIds = collectSubtreeIds(tree, node.id);

        dispatch({
          type: SubItemTreeActionType.REMOVE_NODE,
          data: { id: node.id },
        });

        if (selectedId && removedIds.includes(selectedId)) {
          setElementId(null);
        }

        const parentId = parentOf(tree, node.id) ?? rootId;
        if (tree.status[parentId] === 'loaded') {
          await loadNode(parentId);
        }

        await refreshChangeTypes(R.without(removedIds, nodeIds));
      } else {
        const affectedIds = R.uniq([
          parentOf(tree, node.id) ?? rootId,
          ...collectSubtreeIds(tree, node.id),
        ]).filter(id => tree.status[id] === 'loaded');

        const details = await refetchNodes(affectedIds);

        const staleIds = affectedIds.flatMap(id =>
          collectSubtreeIds(tree, id).filter(childId => childId !== id),
        );

        await refreshChangeTypes(
          R.union(
            R.without(staleIds, nodeIds),
            details.flatMap(detailChildIds),
          ),
        );
      }

      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATED_TEXT,
        autoHideDuration: 3000,
      });
    } catch (error) {
      if (getErrorCode(error) === 422) {
        Snackbar.dismiss();
        setApprovalErrorOpen(true);
      } else {
        Snackbar.error({
          message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
        });
      }
    }
  };

  const approveChanges = useStableCallback((node: AssetElement) =>
    applyChanges('approve', node),
  );
  const rejectChanges = (node: AssetElement) => applyChanges('reject', node);

  const batchCreateElements = async (input: AssetElementsCreateInput) => {
    Snackbar.info({
      message: textConstants.GENERIC_APPLICATION_UPDATING_TEXT,
    });

    try {
      await assetElementsCreate({ variables: { input } });

      const parent = await loadNode(input.assetId);
      await refreshChangeTypes(
        R.union(nodeIds, parent ? detailChildIds(parent) : []),
      );

      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATED_TEXT,
        autoHideDuration: 3000,
      });
    } catch (error) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  // A move/replace can change any loaded parent's children (source and
  // destination), so refetch everything on screen when the modal closes.
  const onMoveOrReplaceClose = async () => {
    setActiveModal(null);

    try {
      await refreshSubItemTree();
    } catch (error) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const updateElementAttributes = async (
    id: string,
    attributes: SubItemAttributeUpdate,
  ) => {
    Snackbar.info({
      message: textConstants.GENERIC_APPLICATION_UPDATING_TEXT,
    });

    try {
      const { data } = await assetUpdate({
        variables: {
          input: {
            id,
            attributes: R.map(
              attribute => ({ value: attribute.value }),
              attributes,
            ),
          },
        },
      });

      dispatch({
        type: SubItemTreeActionType.NODE_LOADED,
        data: { id, detail: data.assetUpdate },
      });

      await refreshChangeTypes(nodeIds);

      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATED_TEXT,
        autoHideDuration: 3000,
      });
    } catch (error) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const applyAttributeChange = async (
    action: 'approve' | 'reject',
    input: AssetAttributeChangeInput,
  ) => {
    Snackbar.info({
      message: textConstants.GENERIC_APPLICATION_UPDATING_TEXT,
    });

    try {
      const mutation =
        action === 'approve'
          ? assetAttributeChangeApprove
          : assetAttributeChangeReject;
      await mutation({ variables: { input } });
      await loadNode(input.assetId);
      await refreshChangeTypes(nodeIds);

      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATED_TEXT,
        autoHideDuration: 3000,
      });
    } catch (error) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const permissions = useMemo<SubItemPermissions>(() => {
    const hasElementWriteRole = isOrgAdminRequiredForAssetAddition
      ? hasRolePermissions(ROLE_SETS.ORG_ADMIN)
      : true;

    return {
      canUpdate:
        hasElementWriteRole &&
        hasAccess(PolicyAction.UPDATE, rootAsset.actions),
      canRename: hasAccess(PolicyAction.RENAME, rootAsset.actions),
      canManageChanges: hasAccess(
        PolicyAction.MANAGE_INVENTORY_CHANGES,
        rootAsset.actions,
      ),
      moveEnabled:
        Boolean(preferences?.inventory?.moveElementEnabled) &&
        hasAccess(PolicyAction.MOVE_ELEMENT, rootAsset.actions),
      replaceEnabled:
        Boolean(preferences?.inventory?.replaceElementEnabled) &&
        hasAccess(PolicyAction.REPLACE_ELEMENT, rootAsset.actions),
      canUpdateAttributes: hasAccess(PolicyAction.UPDATE, rootAsset.actions),
      canCreateMedia: hasAccess(PolicyAction.CREATE_MEDIA, rootAsset.actions),
      canUpdateMedia: hasAccess(PolicyAction.UPDATE_MEDIA, rootAsset.actions),
      canDeleteMedia: hasAccess(PolicyAction.DELETE_MEDIA, rootAsset.actions),
    };
  }, [rootAsset.actions, preferences]);

  const subItemRootAsset = useMemo<SubItemRootAsset>(
    () => ({
      id: rootAsset.id,
      name: rootAsset.name,
      schemaId: rootAsset.schemaId,
    }),
    [rootAsset.id, rootAsset.name, rootAsset.schemaId],
  );

  return {
    value: {
      rootAsset: subItemRootAsset,
      tree,
      permissions,
      selectedId,
      setElementId,
      onRowClick,
      onToggleGroup,
      scrollToId,
      onScrolled,
      activeModal,
      openModal,
      closeModal: () => setActiveModal(null),
      approvalErrorOpen,
      closeApprovalError: () => setApprovalErrorOpen(false),
      approveChanges,
      rejectChanges,
      renameElement,
      deleteElement,
      batchCreateElements,
      updateElementAttributes,
      applyAttributeChange,
    },
    onMoveOrReplaceClose,
  };
};

export default useInventorySubItemTree;
